import { useState } from 'react';

import type { CreateSupplierBranch } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useAddBranch } from '../../lib/queries/suppliers';
import { AppModal } from '../AppModal';

export interface AddBranchModalProps {
  companyId: string;
  supplierId: string;
  supplierName: string;
  prefill?: Partial<{ branchCode: string; branchName: string; addressTh: string }>;
  onClose: () => void;
}

// Label class reconciled to the app's real convention (`meta`) — the plan named a
// `fld-label` class that does not exist in the codebase (same fix as B1.3).
const labelClass = 'meta';
const labelStyle = { display: 'block', marginBottom: 6 } as const;

export function AddBranchModal({
  companyId,
  supplierId,
  supplierName,
  prefill,
  onClose,
}: AddBranchModalProps) {
  const add = useAddBranch(companyId);
  const [branchCode, setBranchCode] = useState(prefill?.branchCode ?? '');
  const [branchName, setBranchName] = useState(prefill?.branchName ?? '');
  const [addressTh, setAddressTh] = useState(prefill?.addressTh ?? '');

  async function handleSubmit() {
    // RECONCILED with Plan A's real backend (real file wins): the branch create body
    // is `CreateSupplierBranch` (= a Vendor row), NOT the plan's `CreateBranch`
    // (the company-location master, a different shape). It omits only
    // id/supplierId/companyId, so code/nameTh/currency/country/isActive are still
    // required — mirror SupplierFormModal: code='' (service generates it), the
    // branch name carries the entity name, defaults THB/TH/active.
    const data: CreateSupplierBranch = {
      code: '',
      nameTh: supplierName,
      branchCode,
      ...(branchName ? { branchName } : {}),
      ...(addressTh ? { addressTh } : {}),
      currency: 'THB',
      country: 'TH',
      isActive: true,
    };
    await add.mutateAsync({ id: supplierId, data });
    onClose();
  }

  const disabled = branchCode.trim().length === 0 || add.isPending;

  return (
    <AppModal
      title="เพิ่มสาขา"
      subtitle={supplierName}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={disabled}
            onClick={() => void handleSubmit()}
          >
            <Icon name="check" size={14} /> {add.isPending ? 'กำลังบันทึก…' : 'เพิ่มสาขา'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 14 }}>
        <div>
          <label className={labelClass} style={labelStyle}>
            รหัสสาขา
          </label>
          <input
            className="input"
            value={branchCode}
            onChange={(e) => setBranchCode(e.target.value)}
            maxLength={5}
            placeholder="00001"
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>
            ชื่อสาขา
          </label>
          <input
            className="input"
            value={branchName}
            onChange={(e) => setBranchName(e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label className={labelClass} style={labelStyle}>
          ที่อยู่
        </label>
        <input
          className="input"
          value={addressTh}
          onChange={(e) => setAddressTh(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>
      {add.error && (
        <div style={{ color: 'var(--negative)', marginTop: 12 }}>
          เพิ่มสาขาไม่สำเร็จ: {add.error.message}
        </div>
      )}
    </AppModal>
  );
}
